export const RUI_CONTROL_ATTR = "data-rui-control";
export const RUI_FIELD_MANAGED_ATTR = "data-rui-field-managed";
export const RUI_FIELD_LABEL_ATTR = "data-rui-field-label";
export const RUI_FIELD_DESCRIPTION_ATTR = "data-rui-field-description";
export const RUI_FIELD_ERROR_ATTR = "data-rui-field-error";
export const RUI_FIELD_DEFAULT_VALUE_ATTR = "data-default-value";
export const RUI_FORM_DEFAULT_VALUES_ATTR = "data-default-values";
const HOST_CONTROL_TAGS = /* @__PURE__ */new Set(["rui-combobox", "rui-date-field", "rui-date-range-picker", "rui-select", "rui-tag-group", "rui-checkbox", "rui-switch", "rui-radio-group", "rui-slider", "rui-knob", "rui-number-field", "rui-listbox"]);
const CONTROL_SELECTOR = `[${RUI_CONTROL_ATTR}], ${Array.from(HOST_CONTROL_TAGS).join(", ")}`;
export function isNativeTextControl(node) {
  return node.localName === "input" || node.localName === "textarea";
}
function isNativeInput(node) {
  return node.localName === "input";
}
function isNativeTextarea(node) {
  return node.localName === "textarea";
}
const stringValueAdapter = {
  read: host => {
    const value = Reflect.get(host, "value");
    return typeof value === "string" ? value : host.getAttribute("value") ?? "";
  },
  write: (host, value) => {
    const next = value == null ? "" : String(value);
    host.setAttribute("value", next);
    if ("value" in host) {
      Reflect.set(host, "value", next);
    }
  }
};
const numberValueAdapter = {
  read: host => {
    const value = Reflect.get(host, "value");
    if (typeof value === "number") {
      return value;
    }
    const raw = host.getAttribute("value");
    return raw == null || raw === "" ? "" : Number(raw);
  },
  write: (host, value) => {
    const next = value == null ? "" : String(value);
    host.setAttribute("value", next);
    if ("value" in host) {
      Reflect.set(host, "value", value);
    }
  }
};
const booleanValueAdapter = {
  read: host => {
    const input = host.querySelector('input[type="checkbox"]');
    return input?.checked ?? host.hasAttribute("checked");
  },
  write: (host, value) => {
    if (value) {
      host.setAttribute("checked", "");
    } else {
      host.removeAttribute("checked");
    }
  }
};
function readNumberProperty(host, property, attribute) {
  const value = Reflect.get(host, property);
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const raw = host.getAttribute(attribute);
  return raw == null || raw === "" ? "" : Number(raw);
}
function isRangeSlider(host) {
  const variant = Reflect.get(host, "variant");
  return variant === "range" || host.getAttribute("variant") === "range";
}
const sliderValueAdapter = {
  read: host => {
    if (isRangeSlider(host)) {
      return [readNumberProperty(host, "rangeMin", "range-min"), readNumberProperty(host, "rangeMax", "range-max")];
    }
    return numberValueAdapter.read(host);
  },
  write: (host, value) => {
    if (Array.isArray(value) && value.length >= 2) {
      host.setAttribute("variant", "range");
      if ("variant" in host) {
        Reflect.set(host, "variant", "range");
      }
      host.setAttribute("range-min", String(value[0]));
      host.setAttribute("range-max", String(value[1]));
      if ("rangeMin" in host) {
        Reflect.set(host, "rangeMin", value[0]);
      }
      if ("rangeMax" in host) {
        Reflect.set(host, "rangeMax", value[1]);
      }
      return;
    }
    numberValueAdapter.write(host, value);
  }
};
const CONTROL_VALUE_ADAPTERS = /* @__PURE__ */new Map([["rui-checkbox", booleanValueAdapter], ["rui-switch", booleanValueAdapter], ["rui-combobox", stringValueAdapter], ["rui-date-field", stringValueAdapter], ["rui-date-range-picker", stringValueAdapter], ["rui-select", stringValueAdapter], ["rui-radio-group", stringValueAdapter], ["rui-listbox", stringValueAdapter], ["rui-tag-group", stringValueAdapter], ["rui-slider", sliderValueAdapter], ["rui-knob", numberValueAdapter], ["rui-number-field", numberValueAdapter]]);
function forEachFieldContentNode(root, visit) {
  const slotHost = root;
  const visited = /* @__PURE__ */new Set();
  const safeVisit = node => {
    if (visited.has(node)) {
      return;
    }
    visited.add(node);
    visit(node);
  };
  if (typeof slotHost.getSlotElements === "function") {
    for (const node of slotHost.getSlotElements()) {
      safeVisit(node);
    }
  }
  const renderRoot = root.querySelector(".rui-field") ?? root;
  for (const child of Array.from(renderRoot.children)) {
    safeVisit(child);
  }
  for (const node of renderRoot.querySelectorAll("*")) {
    safeVisit(node);
  }
  for (const child of Array.from(root.children)) {
    safeVisit(child);
  }
  const shadow = root.shadowRoot;
  if (shadow) {
    for (const child of Array.from(shadow.children)) {
      safeVisit(child);
    }
  }
}
function queryFieldContent(root, selector) {
  let found = null;
  forEachFieldContentNode(root, node => {
    if (found || !(node instanceof HTMLElement)) {
      return;
    }
    if (node.matches(selector)) {
      found = node;
      return;
    }
    found = node.querySelector(selector);
  });
  return found;
}
function pickPrimaryFieldControl(candidates) {
  if (candidates.length === 0) {
    return null;
  }
  if (candidates.length === 1) {
    return candidates[0];
  }
  const hostControl = candidates.find(candidate => HOST_CONTROL_TAGS.has(candidate.localName));
  const markedControl = candidates.find(candidate => candidate.hasAttribute(RUI_CONTROL_ATTR));
  if (markedControl) {
    return markedControl;
  }
  if (hostControl) {
    return hostControl;
  }
  const active = document.activeElement;
  if (active instanceof HTMLElement) {
    for (const candidate of candidates) {
      if (candidate === active || candidate.contains(active)) {
        return candidate;
      }
    }
  }
  const visible = candidates.filter(el => el.isConnected && el.getClientRects().length > 0 && !el.closest('[hidden],[aria-hidden="true"]'));
  const pool = visible.length > 0 ? visible : candidates.filter(el => el.isConnected);
  return pool[pool.length - 1] ?? candidates[candidates.length - 1];
}
function listFieldControlsInRenderTree(root) {
  const renderRoot = root.querySelector(".rui-field");
  if (!(renderRoot instanceof HTMLElement)) {
    return [];
  }
  return Array.from(renderRoot.querySelectorAll(CONTROL_SELECTOR)).filter(el => root.contains(el));
}
export function findFieldControl(root) {
  const fromRenderTree = pickPrimaryFieldControl(listFieldControlsInRenderTree(root));
  if (fromRenderTree) {
    return fromRenderTree;
  }
  let found = null;
  forEachFieldContentNode(root, node => {
    if (found) {
      return;
    }
    const match = findControlInSubtree(node);
    if (match) {
      found = match;
    }
  });
  if (found) {
    return found;
  }
  const renderRoot = root.querySelector(".rui-field") ?? root;
  return findControlInSubtree(renderRoot);
}
function findControlInSubtree(node) {
  if (!(node instanceof HTMLElement)) {
    return null;
  }
  if (node.matches(`[${RUI_CONTROL_ATTR}]`) || HOST_CONTROL_TAGS.has(node.localName)) {
    return node;
  }
  return node.querySelector(CONTROL_SELECTOR);
}
export function readControlValue(control) {
  if (isNativeInput(control)) {
    const host2 = resolveControlHost(control);
    const adapter = host2 === control ? void 0 : CONTROL_VALUE_ADAPTERS.get(host2.localName);
    if (adapter) {
      return adapter.read(host2);
    }
    if (control.type === "checkbox") {
      return control.checked;
    }
    if (control.type === "number" || control.type === "range") {
      return control.value === "" ? "" : Number(control.value);
    }
    return control.value;
  }
  if (isNativeTextarea(control)) {
    return control.value;
  }
  const host = resolveControlHost(control);
  return CONTROL_VALUE_ADAPTERS.get(host.localName)?.read(host) ?? host.getAttribute("value") ?? "";
}
export function writeControlValue(control, value) {
  if (isNativeInput(control)) {
    const host2 = resolveControlHost(control);
    const adapter = host2 === control ? void 0 : CONTROL_VALUE_ADAPTERS.get(host2.localName);
    if (adapter) {
      adapter.write(host2, value);
      return;
    }
    if (control.type === "checkbox") {
      control.checked = Boolean(value);
      const ce = control.closest("rui-checkbox, rui-switch");
      if (ce) {
        if (value) {
          ce.setAttribute("checked", "");
        } else {
          ce.removeAttribute("checked");
        }
      }
      return;
    }
    control.value = value == null ? "" : String(value);
    return;
  }
  if (isNativeTextarea(control)) {
    control.value = value == null ? "" : String(value);
    return;
  }
  const host = resolveControlHost(control);
  CONTROL_VALUE_ADAPTERS.get(host.localName)?.write(host, value);
}
function resolveControlHost(control) {
  if (control.hasAttribute(RUI_CONTROL_ATTR)) {
    const host = control.closest(Array.from(HOST_CONTROL_TAGS).join(","));
    return host ?? control;
  }
  return control;
}
export function findFieldLabel(root) {
  return queryFieldContent(root, `[${RUI_FIELD_LABEL_ATTR}], label.rui-label`);
}
export function findFieldDescription(root) {
  return queryFieldContent(root, `[${RUI_FIELD_DESCRIPTION_ATTR}]`);
}
export function findFieldError(root) {
  const all = findFieldErrorElements(root);
  return all[0] ?? null;
}
export function findFieldErrorElements(root) {
  const renderRoot = root.querySelector(".rui-field");
  if (renderRoot instanceof HTMLElement) {
    const inRender = Array.from(renderRoot.querySelectorAll(`[${RUI_FIELD_ERROR_ATTR}]`));
    const visible = inRender.filter(el => el.getClientRects().length > 0);
    if (visible.length > 0) {
      return visible;
    }
    if (inRender.length > 0) {
      return inRender;
    }
  }
  const fromDom = Array.from(root.querySelectorAll(`[${RUI_FIELD_ERROR_ATTR}]`));
  if (fromDom.length > 0) {
    return fromDom;
  }
  const single = queryFieldContent(root, `[${RUI_FIELD_ERROR_ATTR}]`);
  return single ? [single] : [];
}
export function wireFieldControlName(controlHost, ariaTarget, name) {
  if (!name) {
    return;
  }
  if (ariaTarget && isNativeTextControl(ariaTarget)) {
    ariaTarget.name = name;
  }
  if (!controlHost) {
    return;
  }
  if (controlHost === ariaTarget) {
    return;
  }
  if (HOST_CONTROL_TAGS.has(controlHost.localName)) {
    controlHost.setAttribute("name", name);
  }
}
function isChecked(input) {
  const checked = Reflect.get(input, "checked");
  return typeof checked === "boolean" ? checked : input.hasAttribute("checked");
}
export function getAriaControlTarget(control) {
  if (isNativeTextControl(control)) {
    return control;
  }
  if (control.hasAttribute(RUI_CONTROL_ATTR)) {
    return control;
  }
  if (control.localName === "rui-slider") {
    return control.querySelector("[data-thumb]:not([hidden])") ?? control;
  }
  const marked = control.querySelector(`[${RUI_CONTROL_ATTR}]`);
  if (marked) {
    return marked;
  }
  if (control.localName === "rui-combobox") {
    return control.querySelector("[data-combobox-input]") ?? control;
  }
  if (control.localName === "rui-date-field") {
    return control.querySelector("[data-date-field-input]") ?? control;
  }
  if (control.localName === "rui-date-range-picker") {
    return control.querySelector("[data-range-start]") ?? control;
  }
  if (control.localName === "rui-select") {
    return control.querySelector("[data-select-trigger]") ?? control;
  }
  if (control.localName === "rui-tag-group") {
    return control.querySelector("[data-tag-list]") ?? control;
  }
  if (control.localName === "rui-number-field") {
    return control.querySelector("[data-number-field-input], input") ?? control;
  }
  if (control.localName === "rui-knob") {
    return control.querySelector("[data-knob-control]") ?? control;
  }
  if (control.localName === "rui-checkbox" || control.localName === "rui-switch") {
    return control.querySelector("input") ?? control;
  }
  if (control.localName === "rui-radio-group") {
    const radios = control.querySelectorAll('input[type="radio"]');
    for (const radio of radios) {
      if (isChecked(radio)) {
        return radio;
      }
    }
    return radios[0] ?? control;
  }
  return control;
}
export function getAriaControlTargets(control) {
  if (control.localName === "rui-slider") {
    const thumbs = Array.from(control.querySelectorAll("[data-thumb]:not([hidden])"));
    return thumbs.length > 0 ? thumbs : [control];
  }
  return [getAriaControlTarget(control)];
}